import { useEffect, useRef, useState } from 'react';
import { LARGEUR_ANALYSE, SEUIL_NETTETE, niveauxDeGris, varianceLaplacien } from '@rh/shared';

/** Côté le plus long d'une page envoyée : lisible à l'impression, léger à l'envoi. */
const COTE_MAX = 2000;
export const PAGES_MAX = 6;

export interface PageScan {
  id: string;
  blob: Blob;
  url: string;
  nettete: number;
  /** Contenu du QR code lu sur la photo, s'il y en a un. */
  qrTexte: string | null;
}

/** Jour local `AAAA-MM-JJ`, pour les champs de date. */
export function aujourdhui(): string {
  const maintenant = new Date();
  return new Date(maintenant.getTime() - maintenant.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function toile(largeur: number, hauteur: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = window.document.createElement('canvas');
  canvas.width = largeur;
  canvas.height = hauteur;
  const contexte = canvas.getContext('2d', { willReadFrequently: true });
  if (!contexte) throw new Error('Canvas indisponible sur cet appareil');
  return [canvas, contexte];
}

/**
 * Photo → page : orientation appliquée, taille ramenée à 2000 px, JPEG. La
 * netteté et le QR code se lisent sur une copie de 1000 px de large ; si le QR
 * y échappe, on le cherche sur l'image entière.
 */
async function preparerPhoto(fichier: File): Promise<PageScan> {
  const image = await createImageBitmap(fichier, { imageOrientation: 'from-image' });
  const echelle = Math.min(1, COTE_MAX / Math.max(image.width, image.height));
  const [canvas, contexte] = toile(Math.round(image.width * echelle), Math.round(image.height * echelle));
  contexte.drawImage(image, 0, 0, canvas.width, canvas.height);
  image.close();

  const blob = await new Promise<Blob>((accepter, refuser) =>
    canvas.toBlob((b) => (b ? accepter(b) : refuser(new Error('Conversion de la photo impossible'))), 'image/jpeg', 0.85),
  );

  const reduction = Math.min(1, LARGEUR_ANALYSE / canvas.width);
  const [petit, contextePetit] = toile(Math.round(canvas.width * reduction), Math.round(canvas.height * reduction));
  contextePetit.drawImage(canvas, 0, 0, petit.width, petit.height);
  const donnees = contextePetit.getImageData(0, 0, petit.width, petit.height);
  const nettete = varianceLaplacien(niveauxDeGris(donnees.data), petit.width, petit.height);

  const { default: jsQR } = await import('jsqr');
  let code = jsQR(donnees.data, donnees.width, donnees.height);
  if (!code && reduction < 1) {
    const plein = contexte.getImageData(0, 0, canvas.width, canvas.height);
    code = jsQR(plein.data, plein.width, plein.height);
  }

  return { id: crypto.randomUUID(), blob, url: URL.createObjectURL(blob), nettete, qrTexte: code?.data ?? null };
}

/** Photos d'un document signé (charte, rapport) : préparation, netteté, QR, envoi. */
export function usePhotosScan() {
  const [pages, setPages] = useState<PageScan[]>([]);
  const [traitement, setTraitement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const courantes = useRef<PageScan[]>([]);
  courantes.current = pages;

  // Les aperçus sont des URL locales : on les libère en quittant l'écran.
  useEffect(() => () => courantes.current.forEach((p) => URL.revokeObjectURL(p.url)), []);

  /** Ajoute les photos choisies et renvoie les textes des QR codes lus. */
  async function ajouter(fichiers: FileList | null): Promise<string[]> {
    if (!fichiers || fichiers.length === 0) return [];
    setErreur(null);
    setTraitement(true);
    try {
      const nouvelles: PageScan[] = [];
      for (const fichier of Array.from(fichiers).slice(0, PAGES_MAX - courantes.current.length)) {
        nouvelles.push(await preparerPhoto(fichier));
      }
      setPages((precedentes) => [...precedentes, ...nouvelles]);
      return nouvelles.map((p) => p.qrTexte).filter((texte): texte is string => texte !== null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Lecture de la photo impossible');
      return [];
    } finally {
      setTraitement(false);
    }
  }

  function retirer(page: PageScan): void {
    URL.revokeObjectURL(page.url);
    setPages((precedentes) => precedentes.filter((p) => p.id !== page.id));
  }

  function formulaire(champs: Record<string, string>): FormData {
    const donnees = new FormData();
    for (const [cle, valeur] of Object.entries(champs)) donnees.append(cle, valeur);
    pages.forEach((page, index) => donnees.append('pages', page.blob, `page-${index + 1}.jpg`));
    return donnees;
  }

  return {
    pages,
    traitement,
    erreur,
    ajouter,
    retirer,
    formulaire,
    floues: pages.filter((p) => p.nettete < SEUIL_NETTETE).length,
  };
}

export type PhotosScan = ReturnType<typeof usePhotosScan>;
